import { useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmt } from "@/components/StatusBadge";
import MiniCalendar from "@/components/MiniCalendar";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const EMPTY = { name: "", role: "", phone: "", email: "", day_rate: "" };

export default function Staffing() {
  const [items, setItems] = useState([]);
  const [calendar, setCalendar] = useState({ shifts: [], fees: [] });
  const [tab, setTab] = useState("roster");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const today = new Date();
  const [calYear, setCalYear] = useState(today.getFullYear());
  const [calMonth, setCalMonth] = useState(today.getMonth());

  const load = () => api.get("/staff").then((r) => setItems(r.data.filter((s) => !s.archived))).catch((e) => toast.error(formatApiError(e)));
  const loadCalendar = () => api.get("/staff-calendar").then((r) => setCalendar(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); loadCalendar(); }, []);

  const openEdit = (s) => {
    setEditing(s);
    setForm({ name: s.name, role: s.role || "", phone: s.phone || "", email: s.email || "", day_rate: s.day_rate });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, day_rate: Number(form.day_rate || 0) };
    try {
      if (editing) await api.patch(`/staff/${editing.id}`, payload);
      else await api.post("/staff", payload);
      toast.success(editing ? "Staff updated" : "Staff member added");
      setOpen(false);
      setEditing(null);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete ${s.name} from the roster?`)) return;
    try {
      await api.delete(`/staff/${s.id}`);
      toast.success("Staff member deleted");
      load();
      loadCalendar();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const togglePaid = async (shift) => {
    try {
      await api.patch(`/bookings/${shift.booking_id}/staff/${shift.id}`, { paid: !shift.paid });
      toast.success(shift.paid ? "Marked unpaid" : `Fee marked paid — ${shift.name}`);
      loadCalendar();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const navigateMonth = (delta) => {
    const d = new Date(calYear, calMonth + delta, 1);
    setCalYear(d.getFullYear());
    setCalMonth(d.getMonth());
  };
  const shiftsByDate = {};
  calendar.shifts.forEach((s) => { (shiftsByDate[s.event_date] = shiftsByDate[s.event_date] || []).push(s); });

  return (
    <div data-testid="staffing-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Staffing</h1>
          <p className="mt-1 text-sm text-muted-foreground">Roster, allocation calendar and fee payment tracking.</p>
        </div>
        <Button data-testid="new-staff-button" onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} className="rounded-md bg-ink hover:bg-ink-light">
          <Plus className="mr-2 h-4 w-4" /> Add Staff
        </Button>
      </div>

      <div className="flex gap-1 overflow-x-auto border-b border-border" data-testid="staffing-tabs">
        {["roster", "calendar", "fees"].map((t) => (
          <button key={t} data-testid={`staffing-tab-${t}`} onClick={() => setTab(t)}
            className={`px-4 py-2 text-xs font-semibold uppercase tracking-[0.15em] transition-colors duration-150 ${tab === t ? "border-b-2 border-ink text-ink" : "text-muted-foreground hover:text-ink"}`}>
            {t}
          </button>
        ))}
      </div>

      {tab === "roster" && (
        <div className="border border-border bg-white">
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                {["Name", "Role", "Email", "Phone", "Day Rate", ""].map((h) => (
                  <th key={h} className="px-5 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.id} data-testid={`staff-row-${s.id}`} className="border-b border-border last:border-0 hover:bg-slate-50">
                  <td className="px-5 py-3 font-medium">{s.name}</td>
                  <td className="px-5 py-3">{s.role}</td>
                  <td className="px-5 py-3 text-muted-foreground">{s.email}</td>
                  <td className="px-5 py-3 text-muted-foreground tabular-nums">{s.phone}</td>
                  <td className="px-5 py-3 font-semibold tabular-nums">{fmt(s.day_rate)}</td>
                  <td className="px-5 py-3">
                    <div className="flex gap-2">
                      <Button data-testid={`edit-staff-${s.id}`} size="sm" variant="outline" className="rounded-md" onClick={() => openEdit(s)}><Pencil className="mr-1 h-3.5 w-3.5" /> Edit</Button>
                      <Button data-testid={`delete-staff-${s.id}`} size="sm" variant="outline" className="rounded-md border-red-300 text-red-600 hover:bg-red-50" onClick={() => remove(s)}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-muted-foreground">No staff on the roster yet.</td></tr>}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {tab === "calendar" && (
        <div data-testid="staffing-calendar">
          <MiniCalendar year={calYear} month={calMonth} onNavigate={navigateMonth} testid="staff-calendar"
            dayContent={(ds) => (shiftsByDate[ds] || []).map((s) => (
              <div key={s.id} data-testid={`shift-chip-${s.id}`} className={`px-1.5 py-0.5 text-[11px] font-semibold ${s.paid ? "bg-emerald-100 text-emerald-800" : "bg-blue-100 text-blue-800"}`}>
                {s.name} · {s.booking_ref}
              </div>
            ))}
          />
          <p className="mt-2 text-xs text-muted-foreground">Blue = fee outstanding · Green = paid. Assign shifts from each booking's Staff Rota section.</p>
        </div>
      )}

      {tab === "fees" && (
        <div className="space-y-4" data-testid="staffing-fees">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {calendar.fees.map((f) => (
              <div key={f.staff_id || f.name} className="border border-border bg-white p-5" data-testid={`fee-card-${f.staff_id || f.name}`}>
                <p className="font-heading text-sm font-bold">{f.name}</p>
                <dl className="mt-3 space-y-1.5 text-sm">
                  <div className="flex justify-between"><dt className="text-muted-foreground">Shifts</dt><dd className="tabular-nums">{f.shifts}</dd></div>
                  <div className="flex justify-between"><dt className="text-muted-foreground">Fees booked</dt><dd className="tabular-nums">{fmt(f.booked)}</dd></div>
                  <div className="flex justify-between"><dt className="text-muted-foreground">Paid</dt><dd className="tabular-nums text-brand">{fmt(f.paid)}</dd></div>
                  <div className="flex justify-between border-t border-border pt-1.5"><dt className="font-semibold">Outstanding</dt><dd className="font-bold tabular-nums text-amber-700">{fmt(f.outstanding)}</dd></div>
                </dl>
              </div>
            ))}
            {calendar.fees.length === 0 && <p className="col-span-3 border border-border bg-white px-5 py-10 text-center text-sm text-muted-foreground">No shifts assigned yet.</p>}
          </div>

          <div className="border border-border bg-white">
            <div className="border-b border-border px-5 py-3"><h2 className="font-heading text-sm font-bold uppercase tracking-[0.15em]">Shift fees</h2></div>
            {calendar.shifts.map((s) => (
              <label key={s.id} data-testid={`fee-shift-${s.id}`} className="flex cursor-pointer items-center gap-4 border-b border-border px-5 py-3 text-sm transition-colors duration-150 last:border-0 hover:bg-slate-50">
                <Checkbox checked={s.paid} onCheckedChange={() => togglePaid(s)} data-testid={`fee-paid-${s.id}`} />
                <span className="w-28 tabular-nums text-muted-foreground">{s.event_date}</span>
                <span className="flex-1 font-medium">{s.name} <span className="text-muted-foreground">· {s.role} · {s.booking_ref}</span></span>
                <span className="font-semibold tabular-nums">{fmt(s.fee)}</span>
                <span className={`text-[10px] font-semibold uppercase tracking-[0.15em] ${s.paid ? "text-emerald-700" : "text-amber-700"}`}>{s.paid ? "Paid" : "Owed"}</span>
              </label>
            ))}
            {calendar.shifts.length === 0 && <p className="px-5 py-6 text-sm text-muted-foreground">Assign staff to bookings to track their fees.</p>}
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-white" data-testid="staff-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Staff Member" : "Add Staff Member"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5"><Label>Name</Label><Input data-testid="staff-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Role</Label><Input data-testid="staff-role-input" placeholder="Head Chef" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Day rate (£)</Label><Input data-testid="staff-rate-input" type="number" min="0" value={form.day_rate} onChange={(e) => setForm({ ...form, day_rate: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input data-testid="staff-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Phone</Label><Input data-testid="staff-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div className="col-span-2"><Button data-testid="staff-submit-button" type="submit" className="w-full rounded-md bg-ink hover:bg-ink-light">{editing ? "Save Changes" : "Add to Roster"}</Button></div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
